/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import React from 'react';
import { useDispatch } from 'react-redux';
import { layerActions } from '@opengeoweb/store';
import { LayerType } from '@opengeoweb/webmap';
import {
  FEATURE_DRAW_SECONDARY_STROKE_COLOR,
  emptyGeoJSON,
} from '@opengeoweb/webmap-react';
import { buildClippedHighlightGeoJSON } from './maskUtils';
import type { DrawingListItem } from '../../store/warningsDrawings/types';

export const MASK_PREVIEW_FILL_OPACITY = 0;

export const getMaskPreviewLayerId = (mapId: string): string =>
  `drawing-mask-preview-${mapId}`;

export const getClippedResultLayerId = (mapId: string): string =>
  `drawing-mask-result-${mapId}`;

export const buildMaskPreviewGeoJSON = (
  mask: DrawingListItem,
): GeoJSON.FeatureCollection => ({
  ...mask.geoJSON,
  features: mask.geoJSON.features.map((feature, index) => ({
    ...feature,
    id: feature.id ?? `mask-${mask.id}-${index}`,
    properties: {
      ...feature.properties,
      isReadonly: true,
      mapFeatureClass: 'default',
      isSelected: false,
      isHoverDisabled: true,
      fill: FEATURE_DRAW_SECONDARY_STROKE_COLOR,
      'fill-opacity': MASK_PREVIEW_FILL_OPACITY,
      'stroke-width': 3,
      stroke: '#F59E0B',
    },
  })),
});

const useReadonlyFeatureLayer = (
  mapId: string,
  layerId: string,
  geojson: GeoJSON.FeatureCollection,
): void => {
  const dispatch = useDispatch();

  React.useEffect(() => {
    if (!mapId) {
      return undefined;
    }

    dispatch(
      layerActions.addLayer({
        mapId,
        layerId,
        layer: {
          geojson: emptyGeoJSON,
          layerType: LayerType.featureLayer,
          isSelectable: false,
          isInEditMode: false,
          enabled: true,
        },
        origin: 'warnings-lib',
      }),
    );

    return (): void => {
      dispatch(layerActions.layerDelete({ layerId, mapId }));
    };
  }, [dispatch, layerId, mapId]);

  React.useEffect(() => {
    if (mapId) {
      dispatch(layerActions.layerChangeGeojson({ layerId, geojson }));
    }
  }, [dispatch, geojson, layerId, mapId]);
};

export const useMaskPreviewLayer = (
  mapId: string,
  mask?: DrawingListItem,
): void => {
  const geojson = React.useMemo(
    () => (mask ? buildMaskPreviewGeoJSON(mask) : emptyGeoJSON),
    [mask],
  );
  useReadonlyFeatureLayer(mapId, getMaskPreviewLayerId(mapId), geojson);
};

export const useClippedResultLayer = (
  mapId: string,
  clippedGeoJSON?: GeoJSON.FeatureCollection,
): void => {
  const geojson = React.useMemo(
    () =>
      clippedGeoJSON
        ? buildClippedHighlightGeoJSON(clippedGeoJSON)
        : emptyGeoJSON,
    [clippedGeoJSON],
  );
  useReadonlyFeatureLayer(mapId, getClippedResultLayerId(mapId), geojson);
};
